import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
} from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";

// A bare "www.example.com" typed into the field would otherwise be written as
// a relative href and resolve against the app's own origin.
function normaliseHref(raw) {
  const href = String(raw || "").trim();
  if (!href) return "";
  if (/^(https?:|mailto:|tel:|#|\/)/i.test(href)) return href;
  return `https://${href}`;
}

/**
 * Toolbar item for TinyMCE's `link` button. Registered as a `render` item
 * because, unlike the other buttons, it needs a popover and local state.
 */
export default function LinkButton({ editor }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [href, setHref] = useState("");

  const isActive = editor.isActive("link");

  const open = (event) => {
    setHref(editor.getAttributes("link").href || "");
    setAnchorEl(event.currentTarget);
  };

  const close = () => setAnchorEl(null);

  const apply = () => {
    const value = normaliseHref(href);
    const chain = editor.chain().focus().extendMarkRange("link");
    if (value) chain.setLink({ href: value }).run();
    else chain.unsetLink().run();
    close();
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    close();
  };

  return (
    <>
      <Tooltip title={isActive ? "Edit link" : "Insert link"}>
        <span>
          <IconButton
            size="small"
            color={isActive ? "primary" : "default"}
            // Keep the editor's selection alive while the button is pressed.
            onMouseDown={(event) => event.preventDefault()}
            onClick={open}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box display="flex" alignItems="center" p={1} style={{ gap: 8 }}>
          <TextField
            autoFocus
            size="small"
            variant="outlined"
            placeholder="https://"
            value={href}
            onChange={(event) => setHref(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                apply();
              }
              // Swallow Escape so it closes the popover and not the surrounding
              // MUI Dialog that the notice and factsheet editors live in.
              if (event.key === "Escape") {
                event.stopPropagation();
                close();
              }
            }}
            style={{ minWidth: 260 }}
          />
          <Button size="small" color="primary" onClick={apply}>
            Apply
          </Button>
          {isActive && (
            <Button size="small" onClick={remove}>
              Remove
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
}
